/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useState, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import convert from 'xml-js';
import ReactDataSheet from 'react-datasheet';
import { toast } from 'react-toastify';

import { Button, Col } from 'react-bootstrap';
import {
  Checkbox,
  Modal,
  Button as AntButton,
  Divider,
  Upload,
  message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import { SPDxPlot } from 'components/plots';
import ManufactureForm from 'components/metaDataForm';
import EditAlphaOpicData from 'components/tabs/luox/editAlphaOpicData';
import { LoadingScreen } from 'components/loadingScreen';
import { getLuoxData, convertLuoxData } from 'utils';

import { setReportData, setLibraryData, setLuoxData } from 'redux/actions';

const handleSpdxFileUpload = (spdxFile) => {
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.onerror = () => {
      reader.onabort();
      reject(new DOMException('Problem parsing input file.'));
    };

    reader.onload = () => {
      resolve(reader.result);
    };
    reader.readAsText(spdxFile);
  });
};

export const computeSpdxData = async (spdxFile) => {
  let spdxData = null;
  let wavelength = [];
  let spd = [];
  let gridItems = [];
  let metadata = {};
  let result = {};

  try {
    spdxData = await handleSpdxFileUpload(spdxFile);
  } catch (e) {
    console.log(e);
  }

  let domParser = new DOMParser();
  let dom = domParser.parseFromString(spdxData, 'application/xml');

  const errorMessage = dom.documentElement
    .getElementsByTagName('parsererror')[0]
    ?.getElementsByTagName('div')[0]?.innerHTML;

  if (errorMessage === undefined) {
    let spdxJson = convert.xml2json(spdxData, {
      compact: false,
      spaces: 4,
    });

    spdxJson = JSON.parse(spdxJson);
    let elementsArray = [];
    spdxJson['elements'].forEach((spdx) => {
      if (spdx['type'] === 'element') {
        elementsArray = spdx['elements'];
      }
    });

    elementsArray[0]['elements'].forEach((element) => {
      switch (element.name) {
        case 'Manufacturer':
          Object.assign(metadata, {
            manufacturer: element.elements ? element.elements[0].text : '',
          });
          break;
        case 'CatalogNumber':
          Object.assign(metadata, {
            model: element.elements ? element.elements[0].text : '',
          });
          break;
        case 'Description':
          Object.assign(metadata, {
            description: element.elements ? element.elements[0].text : '',
          });
          break;
        case 'FileCreator':
        case 'DocumentCreator':
          Object.assign(metadata, {
            creator: element.elements ? element.elements[0].text : '',
          });
          break;
        case 'UniqueIdentifier':
          Object.assign(metadata, {
            identifier: element.elements ? element.elements[0].text : '',
          });
          Object.assign(metadata, { measurement: 'Unknown' });
          break;
        case 'Laboratory':
          Object.assign(metadata, {
            laboratory: element.elements ? element.elements[0].text : '',
          });
          break;
        case 'ReportNumber':
          Object.assign(metadata, {
            report_number: element.elements ? element.elements[0].text : '',
          });
          break;
        case 'ReportDate':
          Object.assign(metadata, {
            report_date: element.elements ? element.elements[0].text : '',
          });
          break;
        case 'DocumentCreationDate':
          Object.assign(metadata, {
            creation_date: element.elements ? element.elements[0].text : '',
          });
          break;
        case 'Comments':
          Object.assign(metadata, {
            comments: element.elements ? element.elements[0].text : '',
          });
          break;

        default:
          break;
      }
    });

    elementsArray[1]['elements'].forEach((element) => {
      if (element.name === 'SpectralQuantity') {
        Object.assign(metadata, {
          spectral_quantity: element.elements[0].text,
        });
      }
    });

    elementsArray[1]['elements'].forEach((element) => {
      if (element.name === 'SpectralData') {
        wavelength.push(parseInt(element.attributes.wavelength));
        spd.push(parseFloat(element.elements[0].text).toFixed(6));
      }
    });

    const wavelengthStart = wavelength[0];
    const wavelengthEnd = wavelength[wavelength.length - 1];
    const step = wavelength[1] - wavelength[0];

    // Chopping of extra spd values below 380 and above 780 wavelengths
    if (wavelengthStart < 380 || wavelengthEnd > 780) {
      let message =
        'The required wavelength range is 380 - 780 nm. Data outside this range has not been used in the calculation.';
      // setPasteDataWarnings([...pasteDataWarnings, message]);
      alert(message);
    }
    if (wavelengthStart < 380) {
      const spdsToRemoveFromStart = Math.round((380 - wavelengthStart) / step);
      spd.splice(0, spdsToRemoveFromStart);
      wavelength.splice(0, spdsToRemoveFromStart);
    }
    if (wavelengthEnd > 780) {
      const spdsToRemoveFromEnd = Math.round((wavelengthEnd - 780) / step);

      spd.splice(spd.length - spdsToRemoveFromEnd);
      wavelength.splice(wavelength.length - spdsToRemoveFromEnd);
    }

    wavelength.forEach((wavelength, index) => {
      gridItems.push([
        {
          readOnly: true,
          value: wavelength,
          className: 'cell',
        },
        { value: spd[index] },
      ]);
    });

    result = { wavelength, spd, gridItems, metadata };
    return result;
  } else return errorMessage;
};

const FileInput = ({ view }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const luoxData = useSelector((state) => state.dataReducers.luoxData);
  const luoxUnits = useSelector((state) => state.dataReducers.luoxUnits);

  const [loading, setLoading] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [metadata, setMetadata] = useState({
    manufacturer: '',
    model: '',
    description: '',
    creator: '',
    identifier: '',
    measurement: '',
    laboratory: '',
    report_number: '',
    report_date: '',
    creation_date: '',
    comments: '',
    spectral_quantity: '',
  });
  const [tableReady, setTableReady] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [spd, setSpd] = useState([]);
  const [wavelength, setWaveLength] = useState([]);
  const [grid, setGrid] = useState([
    [
      { readOnly: true, value: 'Wavelength (nm)' },
      {
        readOnly: true,
        className: 'cell',
        value: 'Power',
      },
    ],
  ]);
  const [showLuoxModal, setShowLuoxModal] = useState(false);
  const [shouldCalculateLuoxData, setShouldCalculateLuoxData] = useState(false);

  const handleLuoxCalculation = (e) => {
    const luox = e.target.checked;
    setShouldCalculateLuoxData(luox);
    if (luox) {
      const luoxData = getLuoxData(wavelength, spd);
      dispatch(setLuoxData(luoxData));
    } else {
      dispatch(setLuoxData({}));
    }
  };

  const handleSubmit = (submitType) => {
    setLoading(true);
    axios
      .post(`https://spectralcalculator.pnnl.gov/api/calculate`, {
        // .post(`http://localhost:5000/api/calculate`, {
        // .post(`${window.location.origin}/api/calculate`, {
        spd: spd,
        wavelength: wavelength,
      })
      .then((res) => {
        return res.data;
      })
      .then((data) => {
        // rf and rg values we get the backend were reversed, so correcting them here
        if (!data.error) {
          let tm30Data = data.data.tm30;
          let numericalResults = tm30Data.Numerical_Results;

          const duvTest = numericalResults.DUV[0].toFixed(4);
          const cctTest = numericalResults.CCT[0].toFixed(4);
          if (cctTest > 19900) {
            toast.error(
              <div>
                <h3>Error</h3>{' '}
                <p>
                  The CCT is greater than this program was designed to
                  calculate. TM-30 calculations were not completed.
                </p>
              </div>,
              {
                toastId: 'paste-toast-fail-1',
              }
            );
            setLoading(false);
          } else if (duvTest > 0.04 || duvTest < -0.04) {
            toast.error(
              <div>
                <h3>Error</h3>{' '}
                <p>
                  The chromaticity of the SPD is too far off the Planckian
                  locus. TM-30 calculations were not completed.
                </p>
              </div>,
              {
                toastId: 'paste-toast-fail-2',
              }
            );
            setLoading(false);
          } else {
            tm30Data = { ...tm30Data, Numerical_Results: numericalResults };
            const reportData = { id: 1, tm30: tm30Data, metadata: metadata };

            if (submitType === 'calculate') {
              Object.assign(reportData, { luoxData: luoxData });
              dispatch(setReportData(reportData));
              history.push({
                pathname: '/results',
              });
            } else {
              // Standardizing area scale and power scales
              if (shouldCalculateLuoxData) {
                const libraryStandardLuoxUnits = {
                  ...luoxUnits,
                  ...{ areaScale: 'm2', powerScale: 'mW' },
                };
                const modifiedLuoxData = convertLuoxData(
                  luoxData.luoxInput,
                  libraryStandardLuoxUnits
                );
                Object.assign(reportData, {
                  luoxData: {
                    ...modifiedLuoxData,
                    luoxUnits: libraryStandardLuoxUnits,
                  },
                });
              }

              dispatch(setLibraryData([reportData]));
              history.push({
                pathname: '/user-library',
              });
            }
          }
        } else {
          toast.error(
            <div>
              <h3>Error</h3> <p>{data.error}</p>
            </div>,
            {
              toastId: 'paste-toast-fail',
            }
          );
          setLoading(false);
        }
      })
      .catch((error) => {
        alert(error);
        setLoading(false);
      });
  };

  const handleChange = () => {
    const spdxFile = document.getElementById('spdxFile').files[0];
    if (spdxFile) {
      setUploading(true);
      const fileType = spdxFile.name.split('.')[1];

      if (['spdx', 'SPDX'].includes(fileType)) {
        setFileData(spdxFile);
      } else {
        setLoading(false);
        alert('Incorrect file format. Please make sure it is in SPDX format');
      }

      computeSpdxData(spdxFile).then((result) => {
        if (typeof result === 'string') {
          setUploading(false);
          alert(
            ` ${result} Please make sure the uploaded file is in correct format. You can click on the info icon for more information.`
          );
        } else {
          setTableReady(true);
          setSpd(result.spd);
          setWaveLength(result.wavelength);
          setGrid([...grid, ...result.gridItems]);
          setMetadata(result.metadata);
          setUploading(false);
        }
      });
    }
  };

  const outputSPDx = (
    <div>
      <SPDxPlot inputType='library' spd={spd} wavelength={wavelength} />
      <div css={ReactDataSheetStyle}>
        <ReactDataSheet
          data={grid}
          valueRenderer={(cell) => cell.value}
          // onCellsChanged={changes => {
          //   const grid = grid.map(row => [...row]);
          //   changes.forEach(({ cell, row, col, value }) => {
          //     grid[row][col] = { ...grid[row][col], value };
          //   });
          //   setGrid(grid);
          // }}
        />
      </div>
    </div>
  );

  // const props = {
  //   name: 'file',
  //   onChange(info) {
  //     if (info.file.status !== 'uploading') {
  //     }
  //     if (info.file.status === 'done') {
  //       message.success(`${info.file.name} file uploaded successfully`);
  //     } else if (info.file.status === 'error') {
  //       message.error(`${info.file.name} file upload failed.`);
  //     }
  //   },
  // };

  return (
    <Fragment>
      <Col md={6}>
        <input id='spdxFile' type='file' onChange={handleChange} />
        {/* <Upload {...props}>
          <AntButton icon={<UploadOutlined />}>Click to Upload</AntButton>
        </Upload> */}
      </Col>
      {/* <Col md={2} css={ActiveColumnArrowStyle}>
        {view === "Import File" && (
          <Icon icon="arrow-right" iconDisplay="solid" />
        )}
      </Col> */}
      {view === 'import' ? (
        <Col css={OutputColumnStyle(tableReady, loading)} md={5}>
          <Modal
            title='Edit Alpha-Opic Data'
            visible={showLuoxModal}
            onCancel={() => setShowLuoxModal(false)}
            footer={[
              <AntButton key='clear' onClick={() => setShowLuoxModal(false)}>
                Close
              </AntButton>,
            ]}
          ></Modal>
          <Fragment>
            <div>
              <Col xs={6} css={MetaDataFormStyle}>
                <div>
                  <strong>Calculations to Perform:</strong>
                </div>
                <br />
                <Checkbox checked>IES TM-30 Color Rendition</Checkbox>
                <br />
                <Checkbox onChange={handleLuoxCalculation}>
                  CIE S026 Alpha-Opic{' '}
                </Checkbox>
                <Divider css={dividerStyle} />
                <div css={luoxDataContainerStyle(shouldCalculateLuoxData)}>
                  <EditAlphaOpicData measurementName={metadata.identifier} />
                </div>
                <ManufactureForm
                  metadata={metadata}
                  setMetadata={setMetadata}
                />
              </Col>
              <Col xs={6}>
                {outputSPDx}
                <div css={SpinnerStyle}>
                  <LoadingScreen show={loading} />
                </div>
                <br />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-evenly',
                  }}
                >
                  <Button
                    style={{ marginRight: '1rem' }}
                    onClick={() => handleSubmit('calculate')}
                    bsStyle='primary'
                    type='submit'
                  >
                    Calculate
                  </Button>
                  <Button
                    bsStyle='success'
                    onClick={() => handleSubmit('addToLibrary')}
                  >
                    Add to User Library
                  </Button>
                </div>
              </Col>
            </div>
          </Fragment>
        </Col>
      ) : null}
      {!tableReady && view === 'import' && (
        <div>
          {!uploading && (
            <p css={EmptyOutputTextStyle}>
              Use one of the three options to the left (Call from Library,
              Import SPDX, or Paste Data) to initiate a calculation.
            </p>
          )}
          <div css={UploadSpinnerStyle}>
            <LoadingScreen show={uploading} />
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default FileInput;

FileInput.propTypes = {
  view: PropTypes.string,
};

const ReactDataSheetStyle = css`
  max-height: 41rem;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 2rem;

  table {
    width: 100%;
  }
`;

const OutputColumnStyle = (tableReady, loading) => css`
  position: absolute;
  left: 47rem;
  top: -1rem;
  width: 68rem;
  height: 74rem;
  background: white;
  border-radius: 2rem;
  padding: 20px;
  box-shadow: 0px 1px 5px rgba(0, 0, 0, 0.2);
  border: 1px solid #d9d9d9;

  ${!tableReady
    ? css`
        opacity: 0.2;
        pointer-events: none;
      `
    : ''}
`;

const EmptyOutputTextStyle = css`
  position: absolute;
  left: 56rem;
  top: 18.8rem;
  width: 46rem;
  font-size: 20px;
  font-weight: bold;
`;

const UploadSpinnerStyle = css`
  position: absolute;
  left: 60rem;
  top: 0rem;
  width: 40rem;
`;

const MetaDataFormStyle = css`
  overflow-y: scroll;
  height: 68rem;
`;

const SpinnerStyle = css`
  position: absolute;
  right: 12rem;
  top: 0;
  width: 40rem;
`;

const luoxDataContainerStyle = (shouldCalculateLuoxData) => css`
  opacity: ${shouldCalculateLuoxData ? '1' : '0'};
  pointer-events: ${shouldCalculateLuoxData ? 'auto' : 'none'};
  height: ${shouldCalculateLuoxData ? '30rem' : '1rem'};

  transition: all 400ms;
`;

const dividerStyle = css`
  margin: 12px;
`;
